/**
 * Research wiki — root page. Hosts the child <Routes> tree, all of which
 * render inside ResearchLayout (sidebar + main pane):
 *
 *   /research              → ArticleList
 *   /research/new          → ArticleEditor (create)
 *   /research/:slug        → ArticleDetail
 *   /research/:slug/edit   → ArticleEditor (update)
 *
 * The current user's role flags are exposed via `useResearchUser()` so
 * sub-pages can gate UI affordances (New / Edit buttons, etc.) without
 * each route having to plumb `user` props.
 *
 * Wrapped in `LeagueEnvironmentProvider(NBA)` at the App.tsx route level —
 * research is technically league-agnostic, but the NBA provider gives us a
 * working `ExplorerAPI` instance for the future embedded-query render path.
 */

import React, { createContext, useContext, useMemo } from "react";
import { Route, Routes } from "react-router-dom";
import { AmplifyUser } from "@aws-amplify/ui";

import { isResearchAdmin } from "../../../data/ACL";
import ArticleDetail from "./ArticleDetail";
import ArticleList from "./ArticleList";
import ArticleEditor from "./Editor/ArticleEditor";
import ResearchLayout from "./ResearchLayout";

interface ResearchUserContext {
  user: AmplifyUser | undefined;
  isAdmin: boolean;
}

const UserCtx = createContext<ResearchUserContext>({
  user: undefined,
  isAdmin: false,
});

export function useResearchUser(): ResearchUserContext {
  return useContext(UserCtx);
}

interface ResearchProps {
  user?: AmplifyUser;
}

export default function Research({ user }: ResearchProps) {
  React.useEffect(() => {
    const previous = document.title;
    document.title = "Research - Explorer";
    return () => {
      document.title = previous;
    };
  }, []);

  const value = useMemo<ResearchUserContext>(
    () => ({
      user,
      isAdmin: isResearchAdmin(user),
    }),
    [user],
  );

  return (
    <UserCtx.Provider value={value}>
      <Routes>
        <Route element={<ResearchLayout />}>
          <Route index element={<ArticleList />} />
          <Route path="new" element={<ArticleEditor />} />
          <Route path=":slug" element={<ArticleDetail />} />
          <Route path=":slug/edit" element={<ArticleEditor />} />
        </Route>
      </Routes>
    </UserCtx.Provider>
  );
}
